import type { Auth, MultiFactorResolver, User } from 'firebase/auth'

// Identity Platform only proves who someone is. The API trades the ID token
// for the same `jwt` cookie Google login sets, and the Firebase session is
// signed out straight after, so the cookie stays the one session.

type IdpWebConfig = {
  apiKey: string
  authDomain: string
  tenantId: string
  emulatorHost?: string
}

let configPromise: Promise<IdpWebConfig | null> | null = null
let authPromise: Promise<Auth> | null = null

/** `null` when password login isn't configured for this environment. */
export const getIdpConfig = (): Promise<IdpWebConfig | null> => {
  configPromise ??= fetch('/auth/idp/config', { credentials: 'include' })
    .then((res) => (res.ok ? (res.json() as Promise<IdpWebConfig>) : null))
    .catch(() => null)
  return configPromise
}

/**
 * The local Auth emulator supports only SMS as a second factor, so TOTP
 * enrolment can't work against it.
 */
export const isIdpEmulator = async (): Promise<boolean> =>
  Boolean((await getIdpConfig())?.emulatorHost)

const firebaseAuth = (): Promise<Auth> => {
  authPromise ??= (async () => {
    const config = await getIdpConfig()
    if (!config) throw new Error('Password login is not enabled')
    const [{ initializeApp }, { getAuth, connectAuthEmulator }] =
      await Promise.all([import('firebase/app'), import('firebase/auth')])
    const app = initializeApp(
      { apiKey: config.apiKey, authDomain: config.authDomain },
      'polf-idp'
    )
    const auth = getAuth(app)
    auth.tenantId = config.tenantId
    if (config.emulatorHost) {
      // Reach the emulator on the page's own hostname: localhost and 127.0.0.1
      // are different sites to the browser, and a cross-site emulator loses
      // the Google redirect result to storage partitioning.
      const port = config.emulatorHost.split(':').pop()
      connectAuthEmulator(auth, `http://${window.location.hostname}:${port}`, {
        disableWarnings: true,
      })
    }
    return auth
  })()
  return authPromise
}

export type IdpErrorCode =
  | 'wrong-credentials'
  | 'email-not-verified'
  | 'email-not-verified-recently-sent'
  | 'no-access'
  | 'too-many-attempts'
  | 'weak-password'
  | 'email-in-use'
  | 'invalid-code'
  | 'recent-login-required'
  | 'link-invalid'
  | 'other-method'
  | 'cancelled'
  | 'user-mismatch'
  | 'mfa-expired'
  | 'unknown'

export class IdpError extends Error {
  /** `email`: the address the provider signed in, when it matters to the copy. */
  constructor(readonly code: IdpErrorCode, readonly email?: string) {
    super(code)
  }
}

const toIdpError = (err: unknown): IdpError => {
  if (err instanceof IdpError) return err
  const code = (err as { code?: string })?.code
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return new IdpError('wrong-credentials')
    case 'auth/too-many-requests':
      return new IdpError('too-many-attempts')
    case 'auth/weak-password':
    case 'auth/password-does-not-meet-requirements':
      return new IdpError('weak-password')
    case 'auth/email-already-in-use':
      return new IdpError('email-in-use')
    case 'auth/invalid-verification-code':
    case 'auth/missing-code':
      return new IdpError('invalid-code')
    case 'auth/requires-recent-login':
      return new IdpError('recent-login-required')
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
    case 'auth/user-cancelled':
      return new IdpError('cancelled')
    case 'auth/invalid-multi-factor-session':
    case 'auth/missing-multi-factor-session':
      return new IdpError('mfa-expired')
    case 'auth/account-exists-with-different-credential':
      return new IdpError('other-method')
    case 'auth/expired-action-code':
    case 'auth/invalid-action-code':
      return new IdpError('link-invalid')
    default:
      return new IdpError('unknown')
  }
}

const isMfaRequired = (err: unknown) =>
  (err as { code?: string })?.code === 'auth/multi-factor-auth-required'

export type SignInMethod = 'password' | 'google'

export type SignInResult =
  | { kind: 'done'; user: User }
  | {
      kind: 'totp-required'
      resolver: MultiFactorResolver
      /** How the first step went, so the code card can say why it asks. */
      method: SignInMethod
      email?: string
    }

export const signInWithPassword = async (
  email: string,
  password: string
): Promise<SignInResult> => {
  const auth = await firebaseAuth()
  const { signInWithEmailAndPassword, getMultiFactorResolver } = await import(
    'firebase/auth'
  )
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password)
    return { kind: 'done', user: cred.user }
  } catch (err) {
    if (isMfaRequired(err)) {
      return {
        kind: 'totp-required',
        resolver: getMultiFactorResolver(auth, err as never),
        method: 'password',
        email,
      }
    }
    throw toIdpError(err)
  }
}

// ── Google ─────────────────────────────────────────────────────────────

const REDIRECT_PENDING_KEY = 'polf-idp-redirect'

const googleProvider = async () => {
  const { GoogleAuthProvider } = await import('firebase/auth')
  const provider = new GoogleAuthProvider()
  // Always show the account picker: after a "no access" the next attempt must
  // not silently reuse the same, wrong Google account.
  provider.setCustomParameters({ prompt: 'select_account' })
  return provider
}

const mfaOrThrow = async (auth: Auth, err: unknown): Promise<SignInResult> => {
  if (isMfaRequired(err)) {
    const { getMultiFactorResolver } = await import('firebase/auth')
    return {
      kind: 'totp-required',
      resolver: getMultiFactorResolver(auth, err as never),
      method: 'google',
      email: (err as { customData?: { email?: string } }).customData?.email,
    }
  }
  throw toIdpError(err)
}

/** Leaves the page for Google; the result is picked up by `completeRedirectSignIn`. */
export const signInWithGoogle = async (): Promise<void> => {
  const auth = await firebaseAuth()
  const { signInWithRedirect } = await import('firebase/auth')
  try {
    sessionStorage.setItem(REDIRECT_PENDING_KEY, '1')
  } catch {
    // storage blocked: the page just won't show the pending state
  }
  await signInWithRedirect(auth, await googleProvider())
}

/** True while returning from Google, so the page can hold its form back. */
export const isRedirectPending = (): boolean => {
  try {
    return sessionStorage.getItem(REDIRECT_PENDING_KEY) === '1'
  } catch {
    return false
  }
}

/** `null` when the page wasn't opened by a Google redirect. */
export const completeRedirectSignIn =
  async (): Promise<SignInResult | null> => {
    const auth = await firebaseAuth()
    const { getRedirectResult } = await import('firebase/auth')
    try {
      const cred = await getRedirectResult(auth)
      return cred ? { kind: 'done', user: cred.user } : null
    } catch (err) {
      return mfaOrThrow(auth, err)
    } finally {
      try {
        sessionStorage.removeItem(REDIRECT_PENDING_KEY)
      } catch {
        // ignore
      }
    }
  }

const sameEmail = (a?: string | null, b?: string | null) =>
  Boolean(a && b && a.toLowerCase() === b.toLowerCase())

/**
 * Re-checks a Google user inside a sheet; a popup keeps the sheet's state.
 * The popup can sign in any Google account, so one that isn't `expectedEmail`
 * is refused before anything is changed on it.
 */
export const reauthWithGoogle = async (
  expectedEmail: string
): Promise<SignInResult> => {
  const auth = await firebaseAuth()
  const { signInWithPopup } = await import('firebase/auth')
  let result: SignInResult
  try {
    const cred = await signInWithPopup(auth, await googleProvider())
    result = { kind: 'done', user: cred.user }
  } catch (err) {
    result = await mfaOrThrow(auth, err)
  }
  const email = result.kind === 'done' ? result.user.email : result.email
  if (!sameEmail(email, expectedEmail)) {
    await signOutIdp()
    throw new IdpError('user-mismatch', email ?? undefined)
  }
  return result
}

export const resolveTotp = async (
  resolver: MultiFactorResolver,
  code: string
): Promise<User> => {
  const { TotpMultiFactorGenerator } = await import('firebase/auth')
  const hint = resolver.hints.find(
    (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
  )
  if (!hint) throw new IdpError('unknown')
  try {
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(
      hint.uid,
      code
    )
    const cred = await resolver.resolveSignIn(assertion)
    return cred.user
  } catch (err) {
    throw toIdpError(err)
  }
}

const postJson = (path: string, body: object) =>
  fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

/**
 * What the API did with a send request. `recently-sent` means a link went out
 * under a minute ago and none was sent now; it says nothing about accounts.
 */
export type EmailSendStatus = 'sent' | 'recently-sent'

const readSendStatus = async (res: Response): Promise<EmailSendStatus> => {
  if (!res.ok) throw new IdpError('unknown')
  const body = (await res.json().catch(() => null)) as {
    status?: EmailSendStatus
  } | null
  return body?.status === 'recently-sent' ? 'recently-sent' : 'sent'
}

/** Our API sends the confirmation email, through the same templates as all POLF mail. */
const requestVerificationEmail = async (user: User): Promise<EmailSendStatus> =>
  readSendStatus(
    await postJson('/auth/idp/verify-email', {
      idToken: await user.getIdToken(),
    })
  )

/** Trades the Firebase user for our session cookie; returns where to go. */
export const startSession = async (user: User): Promise<string> => {
  if (!user.emailVerified) {
    // A sign-in attempt before confirming re-sends the link (rate-limited
    // server-side), so an expired or lost email is never a dead end.
    const status = await requestVerificationEmail(user).catch(
      () => 'sent' as const
    )
    await signOutIdp()
    throw new IdpError(
      status === 'recently-sent'
        ? 'email-not-verified-recently-sent'
        : 'email-not-verified'
    )
  }
  const res = await postJson('/auth/idp/session', {
    idToken: await user.getIdToken(true),
  })
  await signOutIdp()
  if (res.status === 401)
    throw new IdpError('no-access', user.email ?? undefined)
  if (!res.ok) throw new IdpError('unknown')
  const { redirect } = (await res.json()) as { redirect: string }
  return redirect
}

export const createPasswordAccount = async (
  email: string,
  password: string
): Promise<void> => {
  const auth = await firebaseAuth()
  const { createUserWithEmailAndPassword } = await import('firebase/auth')
  try {
    const cred = await createUserWithEmailAndPassword(auth, email, password)
    await requestVerificationEmail(cred.user)
  } catch (err) {
    throw toIdpError(err)
  } finally {
    await signOutIdp()
  }
}

/** Always resolves for a well-formed address; the API never says who has an account. */
export const sendPasswordReset = async (
  email: string
): Promise<EmailSendStatus> =>
  readSendStatus(await postJson('/auth/idp/password-reset', { email }))

// ── Links from our emails ───────────────────────────────────────────────

// An oobCode is single-use, so a second apply (a re-mounted effect, a double
// click) must reuse the first attempt rather than burn the code and fail.
const appliedCodes = new Map<string, Promise<void>>()

export const applyEmailVerification = (oobCode: string): Promise<void> => {
  let attempt = appliedCodes.get(oobCode)
  if (!attempt) {
    attempt = (async () => {
      const auth = await firebaseAuth()
      const { applyActionCode } = await import('firebase/auth')
      try {
        await applyActionCode(auth, oobCode)
      } catch (err) {
        throw toIdpError(err)
      }
    })()
    appliedCodes.set(oobCode, attempt)
  }
  return attempt
}

/** Returns the email the reset link belongs to. */
export const checkPasswordResetCode = async (oobCode: string) => {
  const auth = await firebaseAuth()
  const { verifyPasswordResetCode } = await import('firebase/auth')
  try {
    return await verifyPasswordResetCode(auth, oobCode)
  } catch (err) {
    throw toIdpError(err)
  }
}

export const completePasswordReset = async (
  oobCode: string,
  newPassword: string
) => {
  const auth = await firebaseAuth()
  const { confirmPasswordReset } = await import('firebase/auth')
  try {
    await confirmPasswordReset(auth, oobCode, newPassword)
  } catch (err) {
    throw toIdpError(err)
  }
}

/** `user` must be freshly signed in; Identity Platform refuses otherwise. */
export const changePassword = async (
  user: User,
  newPassword: string
): Promise<void> => {
  const { updatePassword } = await import('firebase/auth')
  try {
    await updatePassword(user, newPassword)
  } catch (err) {
    throw toIdpError(err)
  }
}

/**
 * Adds a password to an account that already signs in with Google. Google has
 * proved the address, so the password works at once, with no confirmation email.
 * `user` must be freshly signed in.
 */
export const addPasswordToAccount = async (
  user: User,
  password: string
): Promise<void> => {
  // updatePassword adds a password to a federated-only user. linkWithCredential
  // goes through signUp, which refuses an email the tenant already knows.
  const { updatePassword } = await import('firebase/auth')
  try {
    await updatePassword(user, password)
  } catch (err) {
    throw toIdpError(err)
  }
}

export const signOutIdp = async (): Promise<void> => {
  if (!authPromise) return
  const auth = await authPromise
  const { signOut } = await import('firebase/auth')
  await signOut(auth)
}

// ── Two-step verification (TOTP) ────────────────────────────────────────

export type TotpEnrolment = {
  secretKey: string
  otpauthUrl: string
  confirm: (code: string) => Promise<void>
}

export const hasTotp = async (user: User): Promise<boolean> => {
  const { multiFactor, TotpMultiFactorGenerator } = await import(
    'firebase/auth'
  )
  return multiFactor(user).enrolledFactors.some(
    (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
  )
}

const ISSUER_BY_HOST: Record<string, string> = {
  'app.polf.no': 'POLF',
  'app-test.polf.no': 'POLF staging',
  'app-experimental.polf.no': 'POLF experimental',
}

/**
 * The name the authenticator app files the entry under. Each environment has
 * its own tenant and so its own secret; the label keeps them apart in the app.
 */
const totpIssuer = () =>
  ISSUER_BY_HOST[window.location.hostname] ?? `POLF ${window.location.hostname}`

export const beginTotpEnrolment = async (
  user: User,
  accountLabel: string
): Promise<TotpEnrolment> => {
  const { multiFactor, TotpMultiFactorGenerator } = await import(
    'firebase/auth'
  )
  try {
    const session = await multiFactor(user).getSession()
    const secret = await TotpMultiFactorGenerator.generateSecret(session)
    return {
      secretKey: secret.secretKey,
      otpauthUrl: secret.generateQrCodeUrl(accountLabel, totpIssuer()),
      confirm: async (code: string) => {
        try {
          const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
            secret,
            code
          )
          await multiFactor(user).enroll(assertion, 'Authenticator')
        } catch (err) {
          throw toIdpError(err)
        }
      },
    }
  } catch (err) {
    throw toIdpError(err)
  }
}

export const removeTotp = async (user: User): Promise<void> => {
  const { multiFactor, TotpMultiFactorGenerator } = await import(
    'firebase/auth'
  )
  const factor = multiFactor(user).enrolledFactors.find(
    (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
  )
  if (!factor) return
  try {
    await multiFactor(user).unenroll(factor)
  } catch (err) {
    throw toIdpError(err)
  }
}
